import authored from '../data/199-feeling-experiences.json' with { type: 'json' };
import inspired from '../data/197-feeling-items-inspired.json' with { type: 'json' };
import energized from '../data/198-feeling-items-energized.json' with { type: 'json' };
import { feelingItem } from './44-feeling-items.mjs';

export const FEELING_EXPERIENCE_VERSION = 'feeling-experiences-0.1.0';
const canonical = [...inspired.posts, ...energized.posts];
const artwork = new Map(authored.artwork.map((art) => [art.image, Object.freeze({ ...art })]));
const experiences = new Map();
const signature = (post) => {
  const config = feelingItem(post?.feelingItem);
  if (!config || typeof post.title !== 'string' || typeof post.image !== 'string') return null;
  return JSON.stringify([
    post.title,
    post.image,
    config.version,
    config.mood,
    config.shape,
    config.prompt,
  ]);
};
if (authored.version !== FEELING_EXPERIENCE_VERSION || authored.experiences.length !== 20)
  throw new TypeError('Invalid authored feeling experiences.');
for (const item of authored.experiences) {
  const post = canonical.find((candidate) => candidate.id === item.activityId);
  if (!post || post.feelingItem.mood !== item.mood || item.stages.length !== 4)
    throw new TypeError('Invalid feeling experience binding.');
  let offsetMs = 0;
  const stages = item.stages.map((stage) => {
    const art = artwork.get(stage.image);
    if (
      !art ||
      !art.alt ||
      !art.credit ||
      !art.license ||
      !['label', 'title', 'text'].every(
        (key) => typeof stage[key] === 'string' && stage[key].trim(),
      ) ||
      !Number.isSafeInteger(stage.durationMs) ||
      stage.durationMs < 8000 ||
      stage.durationMs > 20000
    )
      throw new TypeError('Invalid authored feeling stage.');
    const result = Object.freeze({ ...stage, artwork: art, offsetMs });
    offsetMs += stage.durationMs;
    return result;
  });
  const key = signature(post);
  if (experiences.has(key)) throw new TypeError('Duplicate feeling experience binding.');
  experiences.set(
    key,
    Object.freeze({
      version: authored.version,
      activityId: item.activityId,
      mood: item.mood,
      stages: Object.freeze(stages),
      durationMs: offsetMs,
    }),
  );
}

/** Resolve only a canonical activity projected by the trusted editorial boundary.
 * Persisted IDs may differ from source IDs. Changed or malformed metadata returns
 * null so arbitrary user copy cannot become playback instructions. */
export function feelingExperience(post) {
  if (post?.editorial !== true) return null;
  const key = signature(post);
  return key ? (experiences.get(key) ?? null) : null;
}

/** Return a finite story frame for nonnegative elapsed milliseconds. The caller
 * owns the clock and pause policy. Invalid clocks or noncanonical stories fail. */
export function feelingExperienceFrame(experience, elapsedMs) {
  if (
    ![...experiences.values()].includes(experience) ||
    !Number.isFinite(elapsedMs) ||
    elapsedMs < 0
  )
    throw new TypeError('A canonical experience and nonnegative clock are required.');
  const elapsed = Math.min(elapsedMs, experience.durationMs);
  const complete = elapsed === experience.durationMs;
  const stageIndex = complete
    ? 3
    : experience.stages.findIndex((stage) => elapsed < stage.offsetMs + stage.durationMs);
  const stage = experience.stages[stageIndex];
  return {
    stageIndex,
    stage,
    complete,
    elapsedMs: elapsed,
    stageProgress: Math.min(1, (elapsed - stage.offsetMs) / stage.durationMs),
    totalProgress: elapsed / experience.durationMs,
  };
}
